import { NekoStaffSdk } from 'neko-common';
import { nekoStaff } from 'public-contracts';
import { configs } from '../configs';

type AttendanceRecord = nekoStaff.AttendanceRecord;
type AttendanceRecordCreateEntry = nekoStaff.AttendanceRecordCreateEntry;
type AttendanceRecordUpdateEntry = nekoStaff.AttendanceRecordUpdateEntry;

const sdk = new NekoStaffSdk(configs.apiRoot);

export class AttendanceService {
  private attendanceRecord = sdk.attendanceRecord;

  async getAttendanceRecords(): Promise<AttendanceRecord[]> {
    try {
      // Add cache-busting parameter to ensure fresh data
      const cacheBuster = `?t=${Date.now()}&size=1000`;
      const response = await this.attendanceRecord.getAttendanceRecords(
        cacheBuster
      );
      return response;
    } catch (error) {
      console.error('Error fetching attendance records:', error);
      throw error;
    }
  }

  async getAttendanceRecordById(id: string): Promise<AttendanceRecord> {
    try {
      const response = await this.attendanceRecord.getAttendanceRecord(id);
      return response;
    } catch (error) {
      console.error(`Error fetching attendance record with ID ${id}:`, error);
      throw error;
    }
  }

  async getAttendanceRecordsByUserId(
    userId: string
  ): Promise<AttendanceRecord[]> {
    try {
      console.log('🔍 getAttendanceRecordsByUserId called for user:', userId);
      // For now, get all records and filter by userId
      const allRecords = await this.attendanceRecord.getAttendanceRecords();
      console.log('🔍 All attendance records:', allRecords.length);
      console.log(
        '🔍 All records details:',
        allRecords.map((r) => ({
          userId: r.userId,
          date: r.date,
          isIn: r.isIn,
        }))
      );
      const filtered = allRecords.filter((record) => record.userId === userId);
      console.log('🔍 Filtered records for user:', filtered.length);
      return filtered;
    } catch (error) {
      console.error(
        `Error fetching attendance records for user ${userId}:`,
        error
      );
      throw error;
    }
  }

  async createAttendanceRecord(
    entry: AttendanceRecordCreateEntry
  ): Promise<{ insertedId: string }> {
    try {
      console.log(
        '🔍 Calling attendanceRecord.createAttendanceRecord with:',
        entry
      );
      const response = await this.attendanceRecord.createAttendanceRecord(
        entry
      );
      console.log('🔍 Attendance record creation response:', response);
      return response;
    } catch (error) {
      console.error('❌ Error creating attendance record:', error);
      throw error;
    }
  }

  async updateAttendanceRecord(
    id: string,
    entry: AttendanceRecordUpdateEntry
  ): Promise<AttendanceRecord> {
    try {
      const response = await this.attendanceRecord.updateAttendanceRecord(
        id,
        entry
      );
      return response;
    } catch (error) {
      console.error(`Error updating attendance record ${id}:`, error);
      throw error;
    }
  }

  async deleteAttendanceRecord(id: string): Promise<void> {
    try {
      await this.attendanceRecord.deleteAttendanceRecord(id);
    } catch (error) {
      console.error(`Error deleting attendance record ${id}:`, error);
      throw error;
    }
  }

  // Helper method to check in
  async checkIn(
    userId: string,
    location?: string
  ): Promise<{ insertedId: string }> {
    console.log(
      '🔍 Creating check-in record for user:',
      userId,
      'location:',
      location
    );
    const checkinEntry: AttendanceRecordCreateEntry = {
      userId,
      date: new Date().toISOString(),
      isIn: true,
      ratePerHour: 0,
    };

    console.log('🔍 Check-in entry:', checkinEntry);
    const result = await this.createAttendanceRecord(checkinEntry);
    console.log('🔍 Check-in result:', result);
    return result;
  }

  // Helper method to check out
  async checkOut(
    userId: string,
    location?: string
  ): Promise<{ insertedId: string }> {
    console.log(
      '🔍 Creating check-out record for user:',
      userId,
      'location:',
      location
    );
    const checkoutEntry: AttendanceRecordCreateEntry = {
      userId,
      date: new Date().toISOString(),
      isIn: false,
      ratePerHour: 0,
    };

    console.log('🔍 Check-out entry:', checkoutEntry);
    const result = await this.createAttendanceRecord(checkoutEntry);
    console.log('🔍 Check-out result:', result);
    return result;
  }

  // Helper method to get today's attendance for a user
  async getTodayAttendance(userId: string): Promise<AttendanceRecord[]> {
    try {
      const userRecords = await this.getAttendanceRecordsByUserId(userId);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      console.log('🔍 getTodayAttendance - today:', today.toISOString());
      console.log('🔍 getTodayAttendance - userRecords:', userRecords.length);
      console.log(
        '🔍 getTodayAttendance - userRecords dates:',
        userRecords.map((r) => r.date)
      );

      const filtered = userRecords.filter((record) => {
        const recordDate = new Date(record.date);
        const isToday = recordDate >= today;
        console.log(
          `🔍 Record date: ${
            record.date
          }, parsed: ${recordDate.toISOString()}, isToday: ${isToday}`
        );
        return isToday;
      });

      console.log('🔍 getTodayAttendance - filtered:', filtered.length);
      return filtered;
    } catch (error) {
      console.error(
        `Error fetching today's attendance for user ${userId}:`,
        error
      );
      throw error;
    }
  }

  // Break management methods
  async startBreak(
    userId: string
  ): Promise<{ id: string; breakStartTime: string }> {
    try {
      const authHeaders = await this.getAuthHeader();
      console.log('🔍 Auth headers for start break:', authHeaders);

      const response = await fetch(
        `${configs.apiRoot}/attendance_records/start-break`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          body: JSON.stringify({ userId }),
        }
      );

      console.log('🔍 Start break response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 Start break error response:', errorText);
        throw new Error(
          `Failed to start break: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();
      console.log('🔍 Start break success:', result);
      return result;
    } catch (error) {
      console.error('Error starting break:', error);
      throw error;
    }
  }

  async endBreak(
    userId: string
  ): Promise<{ id: string; breakEndTime: string }> {
    try {
      const authHeaders = await this.getAuthHeader();
      console.log('🔍 Auth headers for end break:', authHeaders);

      const response = await fetch(
        `${configs.apiRoot}/attendance_records/end-break`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          body: JSON.stringify({ userId }),
        }
      );

      console.log('🔍 End break response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 End break error response:', errorText);
        throw new Error(
          `Failed to end break: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();
      console.log('🔍 End break success:', result);
      return result;
    } catch (error) {
      console.error('Error ending break:', error);
      throw error;
    }
  }

  async getBreakStatus(userId: string): Promise<{
    isOnBreak: boolean;
    breakStartTime?: string;
    currentBreakDuration: number;
  }> {
    try {
      const authHeaders = await this.getAuthHeader();
      console.log('🔍 Auth headers for get break status:', authHeaders);

      const response = await fetch(
        `${configs.apiRoot}/attendance_records/break-status/${userId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
        }
      );

      console.log('🔍 Get break status response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 Get break status error response:', errorText);
        throw new Error(
          `Failed to get break status: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();
      console.log('🔍 Get break status success:', result);
      return result;
    } catch (error) {
      console.error('Error getting break status:', error);
      throw error;
    }
  }

  private async getAuthHeader(): Promise<Record<string, string>> {
    try {
      // Get the auth token from localStorage - check multiple possible keys
      const tokenRaw =
        localStorage.getItem('auth.accessToken') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('token');

      if (tokenRaw) {
        // Parse the token if it's stored as JSON object
        let token = tokenRaw;
        try {
          const parsed = JSON.parse(tokenRaw);
          if (parsed && typeof parsed === 'object' && parsed.value) {
            token = parsed.value;
          }
        } catch (e) {
          // If parsing fails, use the raw token
          token = tokenRaw;
        }

        console.log('🔍 Found auth token:', token.substring(0, 20) + '...');
        return {
          Authorization: `Bearer ${token}`,
        };
      }

      // Fallback: try to get token from sessionStorage
      const sessionTokenRaw =
        sessionStorage.getItem('auth.accessToken') ||
        sessionStorage.getItem('authToken') ||
        sessionStorage.getItem('token');
      if (sessionTokenRaw) {
        // Parse the token if it's stored as JSON object
        let sessionToken = sessionTokenRaw;
        try {
          const parsed = JSON.parse(sessionTokenRaw);
          if (parsed && typeof parsed === 'object' && parsed.value) {
            sessionToken = parsed.value;
          }
        } catch (e) {
          // If parsing fails, use the raw token
          sessionToken = sessionTokenRaw;
        }

        console.log(
          '🔍 Found session auth token:',
          sessionToken.substring(0, 20) + '...'
        );
        return {
          Authorization: `Bearer ${sessionToken}`,
        };
      }

      console.warn('No auth token found, API calls may fail');
      console.log('Available localStorage keys:', Object.keys(localStorage));
      return {};
    } catch (error) {
      console.error('Error getting auth header:', error);
      return {};
    }
  }
}

export const attendanceService = new AttendanceService();
